import axios from 'axios';
import { toast } from 'react-hot-toast';

const API_URL =
  process.env.REACT_APP_API_URL || 'https://api.pawaschool.net/api/v1';

const apiClient = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

// ----- Refresh State -----
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// ----- Request Interceptor -----
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    const tenantId = localStorage.getItem('tenantId');

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (tenantId) {
      config.headers['x-tenant-id'] = tenantId;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ----- Response Interceptor (with refresh) -----
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Prevent infinite loop if refresh itself fails
    if (originalRequest._retry) {
      console.warn('⛔ Request already retried, rejecting.');
      return Promise.reject(error);
    }

    // Only handle 401 and if we have a refresh token
    if (
      error.response?.status === 401 &&
      localStorage.getItem('refreshToken')
    ) {
      console.log('🔄 Interceptor caught 401, attempting refresh...');
      originalRequest._retry = true;

      if (isRefreshing) {
        console.log('⏳ Refresh already in progress, queueing request...');
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return apiClient(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        console.log('📤 Sending refresh request...');
        const response = await axios.post(
          `${API_URL}/auth/refresh`,
          { refreshToken },
          { headers: { 'Content-Type': 'application/json' } }
        );

        const newAccessToken = response.data?.accessToken;
        const newRefreshToken = response.data?.refreshToken;

        if (!newAccessToken) {
          throw new Error('No access token in refresh response');
        }

        // Store new tokens
        localStorage.setItem('token', newAccessToken);
        if (newRefreshToken) {
          localStorage.setItem('refreshToken', newRefreshToken);
        }
        apiClient.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;

        console.log('✅ Refresh successful, retrying original request...');

        // Process queued requests
        processQueue(null, newAccessToken);

        // Retry original request
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        console.error('❌ Refresh failed:', refreshError.message);
        // Only logout if refresh token is invalid/expired (401)
        if (refreshError.response?.status === 401) {
          console.log('🚪 Refresh token expired, logging out...');
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          localStorage.removeItem('tenantId');
          delete apiClient.defaults.headers.common.Authorization;
          delete apiClient.defaults.headers.common['x-tenant-id'];

          toast.error('Session expired. Please login again.');
          window.location.href = '/login';
        } else {
          // For other errors (network, 500), reject so the user can retry
          console.warn('⚠️ Refresh request failed with non-401:', refreshError.message);
        }
        processQueue(refreshError, null);
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // For non‑401 errors, just reject
    return Promise.reject(error);
  }
);

export default apiClient;